<script lang="ts">
  import { is_privacy_policy_accepted } from "../stores/chat";
  import { scopedLocalStorage } from "../utils/storage";
  interface FormField {
    code: string;
    name: string;
    type: "email" | "text" | "dropdown" | "phone";
    values?: { id: string; value: string }[];
    mandatory: boolean;
    send_as_message?: boolean;
  }

  interface FormData {
    [key: string]: string;
  }

  interface FormErrors {
    [key: string]: string;
  }

  export let accentColor: string;
  export let formFields: FormField[] = [];
  export let title: string =
    "Please fill in the form below before starting the chat";
  export let formData: FormData;
  import {
    validateRequired,
    validateEmail,
    getDeviceType,
  } from "../utils/validators.js";
  import {
    bot_version,
    client_id,
    displayMessage,
    tracking_info,
    messages,
    sendMessage,
    showThinking,
    user_details_form_submitted,
  } from "../stores/chat.js";
  import LoaderIcon from "../utils/assets/loader_icon.svelte";
  import { marked } from "marked";
  import { sanitizeHtml } from "../utils/sanitize";
  import he from "he";
  import { BASE_URL, clientsWithPostFormQuestions } from "../utils/constants";
  import { stripHtmlExtensionFromUrl } from "../utils/functions";
  import { client_config } from "../stores/store.js";
  import SelectDropdown from "./SelectDropdown.svelte";
  import PhoneNumberInput from "./PhoneNumberInput.svelte";

  let errors: FormErrors = {};
  let isLoading = false;
  let phoneCountryCode = "";
  let phoneValidationState: { isValid: boolean; phoneData: any } = {
    isValid: true,
    phoneData: null,
  };
  let isPrivacyPolicyAccepted = $is_privacy_policy_accepted;

  $: {
    formFields.forEach((field) => {
      if (formData[field.code] === undefined) {
        formData[field.code] = "";
      }
      if (errors[field.code] === undefined) {
        errors[field.code] = "";
      }
    });
  }

  $: canSubmit = formFields.every((field) => {
    if (field.mandatory) {
      if (field.type === "email") {
        return !validateEmail(formData[field.code]);
      }
      if (field.type === "phone") {
        return phoneValidationState.isValid && formData[field.code]?.length > 0;
      }
      return !validateRequired(formData[field.code], field.name);
    }
    return true;
  });

  const parseMessage = (message: string): string => {
    try {
      const data = JSON.parse(
        scopedLocalStorage.getItem("user_form_details") || "{}"
      );
      if (formFields?.length > 0 && Object.keys(data)?.length > 0) {
        formFields.forEach((field) => {
          if (data[field.code]) {
            const value = data[field.code]
              .trim()
              .split(/\s+/)
              .map((word: string) => word[0].toUpperCase() + word.slice(1))
              .join(" ");
            message = message.replace(`{{${field.code}}}`, value);
          }
        });
        return message;
      }
    } catch (error) {
      return message;
    }
    return message;
  };

  function validateForm(): boolean {
    let valid = true;
    formFields.forEach((field) => {
      if (field.mandatory) {
        if (field.type === "email") {
          errors[field.code] = validateEmail(formData[field.code]);
        } else if (field.type === "phone") {
          const digitsOnly = formData[field.code]?.replace(/\D/g, "") || "";
          if (digitsOnly.length === 0) {
            errors[field.code] = `${field.name} is required`;
          } else if (!phoneValidationState.isValid) {
            errors[field.code] = "Please enter a valid phone number";
          } else {
            errors[field.code] = "";
          }
        } else {
          errors[field.code] = validateRequired(
            formData[field.code],
            field.name
          );
        }
        if (errors[field.code]) valid = false;
      } else if (formData[field.code]?.length > 0 && field.type === "email") {
        errors[field.code] = validateEmail(formData[field.code]);
        if (errors[field.code]) valid = false;
      } else if (formData[field.code]?.length > 0 && field.type === "phone") {
        if (!phoneValidationState.isValid) {
          errors[field.code] = "Please enter a valid phone number";
          valid = false;
        }
      } else {
        errors[field.code] = "";
      }
    });
    return valid;
  }

  const handleSubmit = async (event: Event) => {
    event.preventDefault();

    if (validateForm()) {
      isLoading = true;
      let payload = {
        ...formData,
      };

      if (phoneCountryCode) {
        payload.phone_country_code = phoneCountryCode;
      }

      delete payload.reason_for_enquiry;

      const trackingInfo = tracking_info?.get();
      const {
        utm_source,
        utm_medium,
        utm_campaign,
        utm_term,
        utm_content,
        gclid,
        fbclid,
        msclkid,
      } = trackingInfo;

      try {
        const response = await fetch(
          `${BASE_URL}/fe/upload_form_data/${client_id.get()}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              // "ngrok-skip-browser-warning": "skip-browser-warning",
            },
            body: JSON.stringify({
              ...payload,
              user_id: scopedLocalStorage.getItem("user_id"),
              tracking_info: {
                url: stripHtmlExtensionFromUrl(window.location.href),
                device_type: getDeviceType(),
                utm_source,
                utm_medium,
                utm_campaign,
                utm_term,
                utm_content,
                gcl_id: gclid,
                fbclid,
                msclkid,
              },
              bot_version: bot_version.get() || "",
            }),
          }
        );

        is_privacy_policy_accepted?.set(isPrivacyPolicyAccepted);
        scopedLocalStorage?.setItem(
          "is_privacy_policy_accepted",
          JSON.stringify(isPrivacyPolicyAccepted)
        );

        if (response?.ok) {
          scopedLocalStorage?.setItem("user_details_form_submitted", "true");
          scopedLocalStorage?.setItem(
            "user_form_details",
            JSON?.stringify(formData)
          );

          user_details_form_submitted?.set(true);

          const clientId = client_id.get() || "";

          if (clientsWithPostFormQuestions?.includes(clientId)) {
            // Send messages for fields with send_as_message: true
            formFields?.forEach((field) => {
              if (field?.send_as_message && formData[field?.code]) {
                displayMessage(formData[field?.code]);
              }
            });
            sendMessage();
            showThinking?.set(true);
          }

          const newMessages = messages
            ?.get()
            ?.map((message) =>
              message?.role === "assistant"
                ? { ...message, message: parseMessage(message?.message) }
                : message
            );
          messages?.set(newMessages);
        } else {
          console.error("Failed to submit form");
          user_details_form_submitted?.set(false);
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        user_details_form_submitted?.set(false);
      } finally {
        isLoading = false;
      }
    }
  };

  function clearError(fieldCode: string): void {
    errors[fieldCode] = "";
  }

  function validateFieldOnBlur(field: FormField): void {
    if (field.type === "email") {
      if (field.mandatory) {
        errors[field.code] = validateEmail(formData[field.code]);
      } else if (formData[field.code]?.length > 0) {
        errors[field.code] = validateEmail(formData[field.code]);
      }
    } else if (field.type === "phone") {
      const digitsOnly = formData[field.code]?.replace(/\D/g, "") || "";
      if (field.mandatory && digitsOnly.length === 0) {
        errors[field.code] = `${field.name} is required`;
      } else if (!phoneValidationState.isValid && digitsOnly.length > 0) {
        errors[field.code] = "Please enter a valid phone number";
      }
    } else if (field.mandatory) {
      errors[field.code] = validateRequired(formData[field.code], field.name);
    }
    errors = { ...errors };
  }

  const formatMessageContent = (content: string): string => {
    if (typeof content !== "string") {
      return "";
    }
    return (
      content?.replace(
        /\n+/g,
        (match) => "<br>".repeat(match?.length) + "\n".repeat(match?.length)
      ) || ""
    );
  };

  $: title = formatMessageContent(title);

  function handlePhoneValidation(event: CustomEvent) {
    phoneValidationState = event.detail;
    const phoneField = formFields.find((field) => field.type === "phone");
    if (phoneField) {
      const digitsOnly = formData[phoneField.code]?.replace(/\D/g, "") || "";

      if (!phoneValidationState.isValid && digitsOnly.length > 0) {
        errors[phoneField.code] = "Please enter a valid phone number";
        errors = { ...errors };
      } else {
        errors[phoneField.code] = "";
        errors = { ...errors };
      }
    }
  }
</script>

<div class="verbaflo-user-details-form-parent-container">
  <img
    src={$client_config?.client_logo_url ||
      "https://vf-assets.verbaflo.com/logo.png"}
    alt="Bot Profile"
    class="verbaflo-user-details-form-bot-image"
  />

  <form class="verbaflo-user-details-form-container" on:submit={handleSubmit}>
    <div class="verbaflo-user-details-form-box">
      <p class="verbaflo-user-details-form-text">
        {#await marked.parse(title) then parsedMessage}
          {@html sanitizeHtml(he.decode(parsedMessage))}
        {/await}
      </p>
      {#each formFields as field}
        <div>
          {#if field.type === "email"}
            <input
              class="verbaflo-user-details-form-input"
              class:verbaflo-user-details-form-input-error={errors[field.code]}
              type="email"
              placeholder={field.name}
              bind:value={formData[field.code]}
              on:focus={() => clearError(field.code)}
              on:input={() => clearError(field.code)}
              on:blur={() => validateFieldOnBlur(field)}
            />
          {:else if field.type === "dropdown"}
            <SelectDropdown
              options={field?.values?.map((item) => ({
                value: item.id,
                label: item.value,
              }))}
              placeholder={field.name}
              selectedValue={formData[field.code]}
              onSelect={(value) => {
                formData[field.code] = value;
              }}
            />
          {:else if field.type === "phone"}
            <PhoneNumberInput
              bind:phoneNumber={formData[field.code]}
              showValidationError={!!errors[field.code]}
              on:select={() => clearError(field.code)}
              on:countryChange={(event) => {
                phoneCountryCode = event.detail.countryCode;
              }}
              on:validationChange={handlePhoneValidation}
              on:blur={() => validateFieldOnBlur(field)}
            />
          {:else}
            <input
              class="verbaflo-user-details-form-input"
              class:verbaflo-user-details-form-input-error={errors[field.code]}
              type="text"
              placeholder={field.name}
              bind:value={formData[field.code]}
              on:focus={() => clearError(field.code)}
              on:input={() => clearError(field.code)}
              on:blur={() => validateFieldOnBlur(field)}
            />
          {/if}
          {#if errors[field.code]}
            <p class="verbaflo-user-details-form-error-text">
              {errors[field.code]}
            </p>
          {/if}
        </div>
      {/each}

      {#if !$is_privacy_policy_accepted && $client_config.consent_capture &&  $client_config?.privacy_policy_link && $messages?.every((msg) => msg?.role === "assistant")}
        <div class="verbaflo-user-details-privacy-policy-container">
          <input
            id="privacy-policy-checkbox"
            class="verbaflo-user-details-privacy-policy-checkbox"
            type="checkbox"
            checked={isPrivacyPolicyAccepted}
            on:change={() => {
              isPrivacyPolicyAccepted = !isPrivacyPolicyAccepted;
            }}
          />
          <label
            class="verbaflo-user-details-privacy-policy-text"
            for="privacy-policy-checkbox"
          >
            This service may use AI to assist with responses. Your data will be
            handled in accordance with
            <button
              class="verbaflo-user-details-privacy-policy-link"
              type="button"
              on:click={() => {
                window.open($client_config?.privacy_policy_link, "_blank");
              }}
            >
              {$client_config?.client_name} Privacy Policy</button
            >
            and
            {#if $client_config?.cookie_policy_link}
              <button
                class="verbaflo-user-details-cookies-policy-link"
                type="button"
                on:click={() => {
                  window.open($client_config?.cookie_policy_link, "_blank");
                }}
            > Cookie Policy.</button>
            {:else}
              <span>cookie policy.</span>
            {/if}
            By continuing, you acknowledge and accept this.
          </label>
        </div>
      {/if}
    </div>
    <button
      type="submit"
      class="verbaflo-user-details-form-submit-button"
      disabled={!canSubmit ||
        isLoading ||
        (!isPrivacyPolicyAccepted && $client_config?.consent_capture && $client_config?.privacy_policy_link)}
      style="--accent-color: {accentColor};"
    >
      <span>
        {$client_config?.form_fields?.start_chatting_text || "Start Chatting"}
      </span>
      {#if isLoading}
        <LoaderIcon />
      {/if}
    </button>
  </form>
</div>

<style>
  :global(#vfwidget .verbaflo-user-details-form-parent-container) {
    display: flex !important;
    align-items: flex-start !important;
    gap: 8px !important;
    width: 100% !important;
  }

  :global(#vfwidget .verbaflo-user-details-form-bot-image) {
    display: inline-block !important;
    width: 24px !important;
    height: 24px !important;
    border-radius: 50% !important;
    align-self: flex-start !important;
    margin-bottom: 16px !important;
    object-fit: cover !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  }

  :global(#vfwidget .verbaflo-user-details-form-container) {
    display: flex !important;
    flex-direction: column !important;
    width: 100% !important;
    gap: 16px !important;
  }

  :global(#vfwidget .verbaflo-user-details-form-box) {
    background-color: #f6f6f6 !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 12px !important;
    border-radius: 8px !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
    padding: 10px 15px !important;
    width: calc(100% - 24px) !important;
  }

  :global(#vfwidget .verbaflo-user-details-form-text) {
    color: black !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    margin: 0 !important;
    padding: 0 !important;
    text-transform: none !important;
    line-height: normal !important;
    text-transform: none !important;
  }

  :global(#vfwidget .verbaflo-user-details-form-text *) {
    color: black !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    margin: 0 !important;
    padding: 0 !important;
    text-transform: none !important;
    line-height: normal !important;
    text-transform: none !important;
  }

  :global(#vfwidget .verbaflo-user-details-form-input) {
    font-size: 14px !important;
    font-weight: 400 !important;
    border: 1px solid #d0d5dd !important;
    border-radius: 8px !important;
    padding: 10px !important;
    width: 100% !important;
    outline: none !important;
    box-shadow: none !important;
    text-transform: none !important;
  }

  :global(#vfwidget .verbaflo-user-details-form-input-error) {
    border: 1px solid #d80000 !important;
  }

  :global(#vfwidget .verbaflo-user-details-form-input:focus) {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
  }

  :global(#vfwidget .verbaflo-user-details-form-error-text) {
    color: #d80000 !important;
    font-size: 12px !important;
    font-weight: 400 !important;
    text-transform: none !important;
    margin: 4px 0 !important;
  }

  :global(#vfwidget .verbaflo-user-details-form-submit-button) {
    cursor: pointer !important;
    border-radius: 8px !important;
    height: 40px !important;
    background-color: var(--accent-color) !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    color: white !important;
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    justify-content: center !important;
    width: calc(100% - 24px) !important;
    border: none !important;
    outline: none !important;
    opacity: 1 !important;
    text-transform: none !important;
    transition: none !important;
  }

  :global(#vfwidget .verbaflo-user-details-form-submit-button:hover),
  :global(#vfwidget .verbaflo-user-details-form-submit-button:active) {
    opacity: 1 !important;
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-user-details-form-submit-button:focus) {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-user-details-form-submit-button:disabled) {
    cursor: not-allowed !important;
    opacity: 1 !important;
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-user-details-privacy-policy-container) {
    display: flex !important;
    align-items: flex-start !important;
    gap: 7px !important;
    margin-bottom: 6px !important;
  }

  :global(#vfwidget .verbaflo-user-details-privacy-policy-checkbox) {
    width: 15px !important;
    height: 15px !important;
    border-radius: 5px !important;
    background-color: #ffffffcc !important;
    border: 1px solid #a3a3a3 !important;
    cursor: pointer !important;
    margin-top: 2px !important;
  }

  :global(#vfwidget .verbaflo-user-details-privacy-policy-text) {
    font-size: 10px !important;
    font-weight: 400 !important;
    color: #535862 !important;
    cursor: pointer !important;
    user-select: none !important;
  }

  :global(#vfwidget .verbaflo-user-details-privacy-policy-link) {
    font-size: 10px !important;
    font-weight: 500 !important;
    color: #3538cd !important;
    text-decoration: underline !important;
    cursor: pointer !important;
    border: none !important;
    background-color: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
    outline: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
  }
  :global(#vfwidget .verbaflo-user-details-cookies-policy-link) {
    font-size: 10px !important;
    font-weight: 500 !important;
    color: #3538cd !important;
    text-decoration: underline !important;
    cursor: pointer !important;
    border: none !important;
    background-color: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
    outline: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
  }
</style>
